import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  Alert,
  Grid,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Print as PrintIcon, Cancel as CancelIcon } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { leaveService, LeaveApplication } from "../services/leaveService";
import ActionConfirmationDialog from "../components/common/ActionConfirmationDialog";

const LeaveDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState<{
    leave: any;
    approvalHistory: any[];
    leaveHistory?: any[];
    eventLogs?: any[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cancellationDialogOpen, setCancellationDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [daysToCancel, setDaysToCancel] = useState<string>("");
  const [cancelDateInput, setCancelDateInput] = useState<string>("");
  const [selectedCancelDates, setSelectedCancelDates] = useState<string[]>([]);
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    actionLabel: string;
    actionColor: "success" | "error" | "warning" | "primary";
    actionType: "approve" | "reject" | "return" | "cancel" | null;
    summary: { label: string; value: string | number }[];
  }>({
    open: false,
    title: "",
    actionLabel: "",
    actionColor: "primary",
    actionType: null,
    summary: [],
  });
  const [actionComments, setActionComments] = useState("");
  const [returnedEditOpen, setReturnedEditOpen] = useState(false);
  const [returnedEditLoading, setReturnedEditLoading] = useState(false);
  const [resubmitLoading, setResubmitLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState<
    Array<{ id: number; name: string }>
  >([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [returnedForm, setReturnedForm] = useState({
    leave_type_id: "",
    start_date: "",
    end_date: "",
    reason: "",
    substitute_id: "",
    people_to_notify: [] as string[],
    include_weekends: false,
    include_holidays: false,
  });
  const [removeAttachmentIds, setRemoveAttachmentIds] = useState<number[]>([]);
  const [newAttachments, setNewAttachments] = useState<File[]>([]);

  useEffect(() => {
    if (id) loadLeave(parseInt(id, 10));
  }, [id]);

  useEffect(() => {
    if (!returnedEditOpen) return;

    const loadReferenceData = async () => {
      try {
        const [typesData, employeesData] = await Promise.all([
          leaveService.getAvailableLeaveTypes(),
          leaveService.getActiveEmployees(),
        ]);
        setLeaveTypes(typesData || []);
        const employeeRows = Array.isArray(employeesData)
          ? employeesData
          : employeesData?.employees || [];
        setEmployees(employeeRows);
      } catch (refError) {
        console.error(
          "Failed to load reference data for returned edit:",
          refError,
        );
      }
    };

    loadReferenceData();
  }, [returnedEditOpen]);

  const isWeekday = (dateStr: string) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day !== 0 && day !== 6;
  };

  const isWithinLeaveRange = (dateStr: string) => {
    if (!leave) return false;
    const target = new Date(dateStr);
    const start = new Date(leave.start_date);
    const end = new Date(leave.end_date);
    target.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return target >= start && target <= end;
  };

  const addCancellationDate = () => {
    if (!cancelDateInput) return;

    if (!isWithinLeaveRange(cancelDateInput)) {
      setError("Selected date must be within the leave period.");
      return;
    }

    if (!isWeekday(cancelDateInput)) {
      setError("Only working days can be selected for cancellation.");
      return;
    }

    if (selectedCancelDates.includes(cancelDateInput)) {
      setError("This date is already selected.");
      return;
    }

    setSelectedCancelDates((prev) => [...prev, cancelDateInput].sort());
    setCancelDateInput("");
    setError(null);
  };

  const removeCancellationDate = (dateToRemove: string) => {
    setSelectedCancelDates((prev) => prev.filter((d) => d !== dateToRemove));
  };

  const getRemainingCancelableDays = () => {
    if (!leave) return 0;
    return Math.max(
      0,
      Number(leave.number_of_days || 0) - Number(leave.cancelled_days || 0),
    );
  };

  const loadLeave = async (leaveId: number) => {
    try {
      setLoading(true);
      const res = await leaveService.getLeaveDetails(leaveId);
      setData(res);
    } catch (err) {
      console.error("Failed to load leave details:", err);
      setError("Failed to load leave details.");
    } finally {
      setLoading(false);
    }
  };

  const leave = data?.leave;
  const approvalHistory = data?.approvalHistory;
  const leaveHistory = data?.leaveHistory || [];
  const eventLogs = data?.eventLogs || [];
  const revisionEntries = leaveHistory.filter(
    (entry: any) => entry.action_type === "REVISED",
  );

  const isCancellationPending = leave?.status === "cancellation_pending";
  const isReturnedApplicant =
    leave?.status === "returned" &&
    user?.employee_code === leave?.employee_code;
  const canApprove =
    user &&
    (user.role === "hr" || user.role === "manager") &&
    (leave?.status === "pending" || isCancellationPending);
  const canReturn = canApprove;
  const canCancel =
    user &&
    (user.role === "hr" || user.employee_code === leave?.employee_code) &&
    ["pending", "partially_cancelled"].includes(leave?.status);
  const canRequestCancellation =
    user &&
    user.employee_code === leave?.employee_code &&
    ["approved", "partially_cancelled"].includes(leave?.status);

  const handleApproveClick = () => {
    if (!leave) return;
    setConfirmDialog({
      open: true,
      title: isCancellationPending
        ? "Approve Cancellation"
        : "Approve Leave Application",
      actionLabel: isCancellationPending ? "Approve Cancellation" : "Approve",
      actionColor: "success",
      actionType: "approve",
      summary: [
        { label: "Applicant", value: leave.employee_name },
        { label: "Leave Type", value: leave.leave_type },
        {
          label: "Duration",
          value: `${new Date(leave.start_date).toLocaleDateString()} to ${new Date(leave.end_date).toLocaleDateString()}`,
        },
        { label: "Days", value: leave.number_of_days },
      ],
    });
  };

  const handleRejectClick = () => {
    if (!leave) return;
    setConfirmDialog({
      open: true,
      title: isCancellationPending
        ? "Reject Cancellation"
        : "Reject Leave Application",
      actionLabel: isCancellationPending ? "Reject Cancellation" : "Reject",
      actionColor: "error",
      actionType: "reject",
      summary: [
        { label: "Applicant", value: leave.employee_name },
        { label: "Leave Type", value: leave.leave_type },
        { label: "Days", value: leave.number_of_days },
      ],
    });
  };

  const handleReturnClick = () => {
    if (!leave) return;
    setConfirmDialog({
      open: true,
      title: "Return Leave Application",
      actionLabel: "Return",
      actionColor: "warning",
      actionType: "return",
      summary: [
        { label: "Applicant", value: leave.employee_name },
        { label: "Leave Type", value: leave.leave_type },
      ],
    });
  };

  const handleConfirmAction = async () => {
    if (!leave || !confirmDialog.actionType) return;

    try {
      setActionLoading(true);
      if (confirmDialog.actionType === "approve") {
        await leaveService.processApproval(
          leave.id,
          "approved",
          actionComments,
        );
        setMessage(
          isCancellationPending
            ? "Cancellation approved successfully."
            : "Application approved successfully.",
        );
      } else if (confirmDialog.actionType === "reject") {
        await leaveService.processApproval(
          leave.id,
          "rejected",
          actionComments,
        );
        setMessage(
          isCancellationPending
            ? "Cancellation rejected."
            : "Application rejected.",
        );
      } else if (confirmDialog.actionType === "return") {
        if (!actionComments.trim()) {
          setError("Comments are required when returning an application.");
          setActionLoading(false);
          return;
        }
        await leaveService.processApproval(leave.id, "return", actionComments);
        setMessage("Application returned to previous level.");
      }

      setConfirmDialog({ ...confirmDialog, open: false });
      setActionComments("");
      await loadLeave(leave.id);
    } catch (err) {
      console.error(err);
      setError(`Failed to ${confirmDialog.actionType} application.`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!leave) return;
    const days = daysToCancel ? parseFloat(daysToCancel) : undefined;
    try {
      setActionLoading(true);
      await leaveService.cancelLeave(leave.id, days);
      setMessage(
        days
          ? `${days} day(s) cancelled successfully.`
          : "Application cancelled.",
      );
      setCancelDialogOpen(false);
      setDaysToCancel("");
      await loadLeave(leave.id);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to cancel application.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRequestCancellation = async () => {
    if (!leave || !cancellationReason.trim()) return;
    let days = daysToCancel ? parseFloat(daysToCancel) : undefined;

    if (selectedCancelDates.length > 0) {
      days = selectedCancelDates.length;
    }

    const remainingDays = getRemainingCancelableDays();
    if (days !== undefined && days > remainingDays) {
      setError(
        `Selected cancellation days (${days}) exceed remaining cancellable days (${remainingDays}).`,
      );
      return;
    }

    try {
      setActionLoading(true);
      const reasonWithDates =
        selectedCancelDates.length > 0
          ? `${cancellationReason.trim()} [Selected dates: ${selectedCancelDates
              .map((d) => new Date(d).toLocaleDateString())
              .join(", ")}]`
          : cancellationReason.trim();

      await leaveService.requestCancellation(leave.id, reasonWithDates, days);
      setMessage(
        days
          ? `Cancellation request for ${days} day(s) submitted successfully.`
          : "Cancellation request submitted successfully.",
      );
      setCancellationDialogOpen(false);
      setCancellationReason("");
      setDaysToCancel("");
      setCancelDateInput("");
      setSelectedCancelDates([]);
      await loadLeave(leave.id);
    } catch (err: any) {
      console.error(err);
      setError(err?.response?.data?.error || "Failed to request cancellation.");
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrintLeave = () => {
    if (!leave) return;
    const history = approvalHistory || [];
    const statusColor =
      leave.status === "approved"
        ? "#2e7d32"
        : leave.status === "rejected"
          ? "#d32f2f"
          : "#ed6c02";
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Please allow popups to print");
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html><html><head>
      <title>Leave Application - ${leave.application_number}</title>
      <style>
        @media print { @page { size: A4; margin: 15mm; } }
        body { font-family: 'Times New Roman', Times, serif; padding: 20px; margin: 0; color: #333; }
        .header { text-align: center; border-bottom: 2px solid #1976d2; padding-bottom: 15px; margin-bottom: 20px; }
        .header h1 { font-size: 18px; margin: 5px 0; color: #1976d2; }
        .header h2 { font-size: 14px; margin: 5px 0; font-weight: normal; }
        .app-number { background: #f5f5f5; padding: 8px 15px; display: inline-block; border-radius: 4px; font-weight: bold; margin-top: 10px; font-size: 13px; }
        .status-badge { display: inline-block; padding: 4px 12px; border-radius: 12px; color: white; font-weight: bold; font-size: 12px; background-color: ${statusColor}; text-transform: uppercase; }
        .section { margin-bottom: 20px; border: 1px solid #e0e0e0; border-radius: 6px; overflow: hidden; }
        .section-title { background: #f5f5f5; padding: 8px 15px; font-weight: bold; font-size: 13px; border-bottom: 1px solid #e0e0e0; }
        .section-content { padding: 12px 15px; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        .info-item { margin-bottom: 6px; }
        .info-label { font-weight: bold; color: #555; font-size: 12px; }
        .info-value { font-size: 13px; margin-top: 2px; }
        .reason-box { background: #fafafa; padding: 10px; border-radius: 4px; font-style: italic; margin-top: 5px; border-left: 3px solid #1976d2; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background: #f5f5f5; font-weight: bold; }
        .footer { margin-top: 40px; display: flex; justify-content: space-between; padding-top: 20px; border-top: 1px solid #e0e0e0; }
        .signature-block { width: 40%; text-align: center; }
        .signature-line { border-top: 1px dotted #333; margin-top: 50px; padding-top: 5px; font-size: 12px; }
        .print-date { text-align: right; font-size: 11px; color: #999; margin-top: 20px; }
        .substitute-box { background: #fff8e1; padding: 10px; border-radius: 4px; border-left: 3px solid #ff9800; }
      </style>
      </head><body>
        <div class="header">
          <h1>University of Vavuniya</h1>
          <h2>Leave Application</h2>
          <div class="app-number">Application No: ${leave.application_number}</div>
          <div style="margin-top: 8px;"><span class="status-badge">${leave.status}</span></div>
        </div>
        <div class="section">
          <div class="section-title">Employee Information</div>
          <div class="section-content"><div class="info-grid">
            <div class="info-item"><div class="info-label">Employee Name</div><div class="info-value">${leave.employee_name || "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Employee Code</div><div class="info-value">${leave.employee_code || "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Department</div><div class="info-value">${leave.department_name || "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Designation</div><div class="info-value">${leave.designation || "N/A"}</div></div>
          </div></div>
        </div>
        <div class="section">
          <div class="section-title">Leave Details</div>
          <div class="section-content"><div class="info-grid">
            <div class="info-item"><div class="info-label">Leave Type</div><div class="info-value">${leave.leave_type || "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Number of Days</div><div class="info-value">${leave.number_of_days || "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Start Date</div><div class="info-value">${leave.start_date ? new Date(leave.start_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "N/A"}</div></div>
            <div class="info-item"><div class="info-label">End Date</div><div class="info-value">${leave.end_date ? new Date(leave.end_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Applied Date</div><div class="info-value">${leave.applied_date ? new Date(leave.applied_date).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "N/A"}</div></div>
            <div class="info-item"><div class="info-label">Status</div><div class="info-value" style="color: ${statusColor}; font-weight: bold;">${(leave.status || "").toUpperCase()}</div></div>
          </div>
          <div style="margin-top: 10px;"><div class="info-label">Reason</div><div class="reason-box">${leave.reason || "No reason provided"}</div></div>
          </div>
        </div>
        ${leave.substitute_name ? `<div class="section"><div class="section-title">Substitute Information</div><div class="section-content"><div class="substitute-box"><div class="info-grid"><div class="info-item"><div class="info-label">Substitute</div><div class="info-value">${leave.substitute_name} (${leave.substitute_code || ""})</div></div><div class="info-item"><div class="info-label">Status</div><div class="info-value">${(leave.substitute_status || "notified").toUpperCase()}</div></div></div></div></div></div>` : ""}
        ${history.length > 0 ? `<div class="section"><div class="section-title">Approval History</div><div class="section-content"><table><thead><tr><th>Action</th><th>Approver</th><th>Comments</th><th>Date</th></tr></thead><tbody>${history.map((h: any) => `<tr><td style="font-weight:bold;text-transform:uppercase">${h.action || ""}</td><td>${h.approver_name || ""}</td><td>${h.comments || "-"}</td><td>${h.created_at ? new Date(h.created_at).toLocaleString() : ""}</td></tr>`).join("")}</tbody></table></div></div>` : ""}
        <div class="footer"><div class="signature-block"><div class="signature-line">Employee Signature</div></div><div class="signature-block"><div class="signature-line">Authorized Signature</div></div></div>
        <div class="print-date">Printed on: ${new Date().toLocaleString()} | Leave Management System - University of Vavuniya</div>
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  };

  const openReturnedEditDialog = () => {
    if (!leave) return;

    let notifyList: string[] = [];
    try {
      const parsed =
        typeof leave.people_to_notify === "string"
          ? JSON.parse(leave.people_to_notify)
          : leave.people_to_notify;
      if (Array.isArray(parsed)) {
        notifyList = parsed.map((notifyId: any) => String(notifyId));
      }
    } catch (parseErr) {
      console.warn(
        "Could not parse people_to_notify for returned edit:",
        parseErr,
      );
    }

    setReturnedForm({
      leave_type_id: String(leave.leave_type_id || ""),
      start_date: String(leave.start_date || "").split("T")[0],
      end_date: String(leave.end_date || "").split("T")[0],
      reason: leave.reason || "",
      substitute_id: leave.substitute_id ? String(leave.substitute_id) : "",
      people_to_notify: notifyList,
      include_weekends: Boolean(leave.include_weekends),
      include_holidays: Boolean(leave.include_holidays),
    });
    setRemoveAttachmentIds([]);
    setNewAttachments([]);
    setReturnedEditOpen(true);
  };

  const handleReturnedFormChange = (field: string, value: any) => {
    setReturnedForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const toggleRemoveAttachment = (attachmentId: number) => {
    setRemoveAttachmentIds((prev) =>
      prev.includes(attachmentId)
        ? prev.filter((id) => id !== attachmentId)
        : [...prev, attachmentId],
    );
  };

  const validateReturnedForm = () => {
    if (
      !returnedForm.leave_type_id ||
      !returnedForm.start_date ||
      !returnedForm.end_date ||
      !returnedForm.reason.trim()
    ) {
      setError("Leave type, dates, and reason are required.");
      return false;
    }
    return true;
  };

  const buildReturnedRevisionPayload = () => {
    const payload = new FormData();
    payload.append("leave_type_id", returnedForm.leave_type_id);
    payload.append("start_date", returnedForm.start_date);
    payload.append("end_date", returnedForm.end_date);
    payload.append("reason", returnedForm.reason.trim());
    payload.append(
      "include_weekends",
      returnedForm.include_weekends ? "1" : "0",
    );
    payload.append(
      "include_holidays",
      returnedForm.include_holidays ? "1" : "0",
    );
    payload.append("substitute_id", returnedForm.substitute_id);
    payload.append(
      "people_to_notify",
      JSON.stringify(returnedForm.people_to_notify || []),
    );

    if (removeAttachmentIds.length > 0) {
      payload.append(
        "remove_attachment_ids",
        JSON.stringify(removeAttachmentIds),
      );
    }

    newAttachments.forEach((file) => {
      payload.append("attachments", file);
    });

    return payload;
  };

  const getReturnedChangesSummary = () => {
    if (!leave) return [] as string[];

    const changedItems: string[] = [];
    if (String(leave.leave_type_id || "") !== returnedForm.leave_type_id) {
      changedItems.push("leave type");
    }
    if (
      String(leave.start_date || "").split("T")[0] !== returnedForm.start_date
    ) {
      changedItems.push("start date");
    }
    if (String(leave.end_date || "").split("T")[0] !== returnedForm.end_date) {
      changedItems.push("end date");
    }
    if ((leave.reason || "").trim() !== returnedForm.reason.trim()) {
      changedItems.push("reason");
    }
    if (String(leave.substitute_id || "") !== returnedForm.substitute_id) {
      changedItems.push("substitute");
    }
    if (
      Boolean(leave.include_weekends) !== Boolean(returnedForm.include_weekends)
    ) {
      changedItems.push("include weekends");
    }
    if (
      Boolean(leave.include_holidays) !== Boolean(returnedForm.include_holidays)
    ) {
      changedItems.push("include holidays");
    }

    if (removeAttachmentIds.length > 0) {
      changedItems.push(`${removeAttachmentIds.length} attachment(s) removed`);
    }
    if (newAttachments.length > 0) {
      changedItems.push(`${newAttachments.length} attachment(s) added`);
    }

    return changedItems;
  };

  const handleReviseReturnedApplication = async () => {
    if (!leave) return;
    if (!validateReturnedForm()) {
      return;
    }

    try {
      setReturnedEditLoading(true);
      setError(null);

      const payload = buildReturnedRevisionPayload();

      await leaveService.reviseReturnedLeave(leave.id, payload);
      setMessage(
        "Returned application updated. Review changes and submit this same application for approval.",
      );
      setReturnedEditOpen(false);
      await loadLeave(leave.id);
    } catch (reviseErr: any) {
      console.error("Failed to revise returned application:", reviseErr);
      setError(
        reviseErr?.response?.data?.error ||
          "Failed to update returned application.",
      );
    } finally {
      setReturnedEditLoading(false);
    }
  };

  const handleSubmitEditedReturnedApplication = async () => {
    if (!leave) return;
    if (!validateReturnedForm()) return;

    const changedItems = getReturnedChangesSummary();
    const summaryText =
      changedItems.length > 0
        ? `\n\nDetected changes:\n- ${changedItems.join("\n- ")}`
        : "\n\nNo explicit field differences were detected.";

    const confirmSubmit = window.confirm(
      `Please confirm submission of the edited version of this returned application.${summaryText}\n\nClick OK to save these edits and submit for approval.`,
    );

    if (!confirmSubmit) {
      return;
    }

    try {
      setReturnedEditLoading(true);
      setResubmitLoading(true);
      setError(null);

      const payload = buildReturnedRevisionPayload();
      await leaveService.reviseReturnedLeave(leave.id, payload);
      await leaveService.resubmitReturnedLeave(leave.id);
      setReturnedEditOpen(false);
      setMessage(
        "Edited version submitted for approval with your confirmed changes.",
      );
      await loadLeave(leave.id);
    } catch (submitErr: any) {
      console.error("Failed to submit edited returned application:", submitErr);
      setError(
        submitErr?.response?.data?.error ||
          "Failed to submit edited returned application.",
      );
    } finally {
      setReturnedEditLoading(false);
      setResubmitLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        ← Back
      </Button>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {message && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading && <Typography>Loading...</Typography>}

          {!loading && leave && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Leave Application Detail
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Application #:</strong> {leave.application_number}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Applicant:</strong> {leave.employee_name} (
                      {leave.employee_code})
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Department:</strong> {leave.department_name}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Status:</strong>{" "}
                      <Box
                        component="span"
                        sx={{
                          fontWeight: "bold",
                          color:
                            leave.status === "approved"
                              ? "success.main"
                              : leave.status === "rejected"
                                ? "error.main"
                                : leave.status === "cancellation_pending"
                                  ? "info.main"
                                  : leave.status === "cancelled"
                                    ? "text.disabled"
                                    : leave.status === "partially_cancelled"
                                      ? "warning.main"
                                      : "warning.main",
                        }}
                      >
                        {leave.status.toUpperCase()}
                      </Box>
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      <strong>Applied On:</strong>{" "}
                      {new Date(leave.applied_date).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  p: 2,
                  backgroundColor: "rgba(0,0,0,0.02)",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Leave Information
                </Typography>
                <Typography>
                  <strong>Type:</strong> {leave.leave_type}
                </Typography>
                <Typography>
                  <strong>Dates:</strong>{" "}
                  {new Date(leave.start_date).toLocaleDateString()} to{" "}
                  {new Date(leave.end_date).toLocaleDateString()}
                </Typography>
                <Typography>
                  <strong>Working Days:</strong> {leave.number_of_days}
                </Typography>
                {leave.cancelled_days > 0 && (
                  <Typography>
                    <strong>Cancelled Days:</strong> {leave.cancelled_days}{" "}
                    <Typography component="span" color="textSecondary">
                      (Active: {leave.number_of_days - leave.cancelled_days}{" "}
                      days)
                    </Typography>
                  </Typography>
                )}
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    <strong>Reason:</strong>
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontStyle: "italic", ml: 1 }}
                  >
                    {leave.reason || "(No reason provided)"}
                  </Typography>
                </Box>
              </Box>

              {leave.attachments && leave.attachments.length > 0 && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(0,0,0,0.02)",
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Attachments
                  </Typography>
                  {leave.attachments.map((attachment: any) => (
                    <Box key={attachment.id} sx={{ mb: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={async () => {
                          try {
                            const token = localStorage.getItem("token");
                            const apiUrl =
                              process.env.REACT_APP_API_URL ||
                              "http://localhost:5002/api";
                            const response = await fetch(
                              `${apiUrl}/attachments/${attachment.id}`,
                              {
                                headers: {
                                  Authorization: `Bearer ${token}`,
                                },
                              },
                            );

                            if (!response.ok) {
                              throw new Error("Failed to download attachment");
                            }

                            // Get the blob and create download link
                            const blob = await response.blob();
                            const url = window.URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.style.display = "none";
                            a.href = url;
                            a.download = attachment.original_filename;
                            document.body.appendChild(a);
                            a.click();
                            window.URL.revokeObjectURL(url);
                            document.body.removeChild(a);
                          } catch (err) {
                            console.error("Download error:", err);
                            alert("Failed to download attachment");
                          }
                        }}
                        sx={{ mr: 1 }}
                      >
                        Download {attachment.original_filename}
                      </Button>
                      <Typography variant="caption" color="textSecondary">
                        ({(attachment.size / 1024).toFixed(1)} KB)
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}

              {isCancellationPending && (
                <Alert severity="info" sx={{ mb: 0 }}>
                  <strong>Cancellation Requested</strong>
                  {leave.cancellation_reason && (
                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                      Reason: "{leave.cancellation_reason}"
                    </Typography>
                  )}
                  {leave.cancellation_requested_date && (
                    <Typography variant="caption" display="block">
                      Requested on:{" "}
                      {new Date(
                        leave.cancellation_requested_date,
                      ).toLocaleString()}
                    </Typography>
                  )}
                </Alert>
              )}

              {isReturnedApplicant && (
                <Alert severity="warning" sx={{ mb: 0 }}>
                  <strong>Application Returned</strong>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    This leave application was returned by an approver. Update
                    this same application, then submit it again. Approval
                    history will be preserved on this record.
                  </Typography>
                </Alert>
              )}

              {leave.substitute_id && (
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: "rgba(255, 152, 0, 0.05)",
                    borderRadius: 1,
                    border: "1px solid rgba(255, 152, 0, 0.2)",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="warning.dark"
                  >
                    Substitute Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {leave.substitute_name} (
                    {leave.substitute_code})
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong>{" "}
                    {leave.substitute_status === "notified"
                      ? "NOTIFIED"
                      : leave.substitute_status?.toUpperCase()}
                  </Typography>
                  {leave.substitute_remarks && (
                    <Typography variant="body2">
                      <strong>Remarks:</strong> "{leave.substitute_remarks}"
                    </Typography>
                  )}
                </Box>
              )}

              {approvalHistory && approvalHistory.length > 0 && (
                <Box>
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Approval History
                  </Typography>
                  <Box sx={{ pl: 2, borderLeft: "2px solid #e0e0e0" }}>
                    {approvalHistory.map((history: any) => (
                      <Box
                        key={history.id}
                        sx={{ mb: 2, position: "relative" }}
                      >
                        <Typography variant="body2">
                          <strong>{history.action.toUpperCase()}</strong> by{" "}
                          {history.approver_name} ({history.approver_role})
                          {history.comments && (
                            <Box
                              component="span"
                              sx={{
                                display: "block",
                                ml: 1,
                                color: "text.secondary",
                                fontSize: "0.85rem",
                              }}
                            >
                              — "{history.comments}"
                            </Box>
                          )}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {new Date(history.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {revisionEntries.length > 0 && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Revision Timeline
                  </Typography>
                  <Box sx={{ pl: 2, borderLeft: "2px solid #e0e0e0" }}>
                    {revisionEntries.map((entry: any) => {
                      let changedFields: Record<string, any> = {};
                      let reasonHistory: {
                        previous_reason?: string;
                        revised_reason?: string;
                        reason_changed?: boolean;
                      } | null = null;
                      if (entry.remarks) {
                        try {
                          const parsed = JSON.parse(entry.remarks);
                          changedFields = parsed.changed_fields || {};
                          reasonHistory = parsed.reason_history || null;
                        } catch {
                          changedFields = {};
                          reasonHistory = null;
                        }
                      }

                      const fieldNames = Object.keys(changedFields);

                      return (
                        <Box key={entry.id} sx={{ mb: 2 }}>
                          <Typography variant="body2" fontWeight="bold">
                            Revised by {entry.actor_name || "Applicant"}
                          </Typography>
                          {fieldNames.length > 0 ? (
                            <Box sx={{ ml: 1, mt: 0.5 }}>
                              {fieldNames.map((field) => (
                                <Typography
                                  key={field}
                                  variant="caption"
                                  display="block"
                                  color="textSecondary"
                                >
                                  {field}:{" "}
                                  {String(changedFields[field]?.from ?? "")}
                                  {" -> "}
                                  {String(changedFields[field]?.to ?? "")}
                                </Typography>
                              ))}
                              {reasonHistory && (
                                <>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                  >
                                    previous_reason:{" "}
                                    {String(
                                      reasonHistory.previous_reason ?? "",
                                    )}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="textSecondary"
                                  >
                                    revised_reason:{" "}
                                    {String(reasonHistory.revised_reason ?? "")}
                                  </Typography>
                                </>
                              )}
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              sx={{ ml: 1 }}
                            >
                              Revision saved.
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary">
                            {new Date(entry.created_at).toLocaleString()}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              )}

              {eventLogs && eventLogs.length > 0 && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    gutterBottom
                  >
                    Event Timeline
                  </Typography>
                  <Box sx={{ pl: 2, borderLeft: "2px solid #e0e0e0" }}>
                    {eventLogs.map((log: any) => (
                      <Box key={log.id} sx={{ mb: 2 }}>
                        <Typography variant="body2">
                          <strong>{(log.action || "").toUpperCase()}</strong>
                          {log.actor_name && ` by ${log.actor_name}`}
                          {log.to_status &&
                            ` → ${log.to_status?.toUpperCase()}`}
                        </Typography>
                        {log.comments && (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ ml: 1 }}
                          >
                            "{log.comments}"
                          </Typography>
                        )}
                        <Typography variant="caption" color="textSecondary">
                          {new Date(log.created_at).toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {canApprove && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleApproveClick}
                      disabled={actionLoading}
                    >
                      {isCancellationPending
                        ? "Approve Cancellation"
                        : "Approve"}
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={handleRejectClick}
                      disabled={actionLoading}
                    >
                      {isCancellationPending ? "Reject Cancellation" : "Reject"}
                    </Button>
                    {!isCancellationPending && (
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={handleReturnClick}
                        disabled={actionLoading}
                      >
                        Return
                      </Button>
                    )}
                  </>
                )}

                {canCancel && (
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => {
                      setDaysToCancel("");
                      setCancelDialogOpen(true);
                    }}
                    disabled={actionLoading}
                  >
                    Cancel Leave
                  </Button>
                )}

                {canRequestCancellation && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={() => {
                      setDaysToCancel("");
                      setCancelDateInput("");
                      setSelectedCancelDates([]);
                      setCancellationDialogOpen(true);
                    }}
                    disabled={actionLoading}
                  >
                    Request Cancellation
                  </Button>
                )}

                {isReturnedApplicant && (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={openReturnedEditDialog}
                      disabled={returnedEditLoading || resubmitLoading}
                    >
                      Edit Returned Application
                    </Button>
                  </>
                )}

                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={handlePrintLeave}
                >
                  Print
                </Button>

                <Button variant="outlined" onClick={() => navigate(-1)}>
                  Back
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={returnedEditOpen}
        onClose={() => setReturnedEditOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Returned Application</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Leave Type"
                value={returnedForm.leave_type_id}
                onChange={(e) =>
                  handleReturnedFormChange("leave_type_id", e.target.value)
                }
              >
                {leaveTypes.map((lt) => (
                  <MenuItem key={lt.id} value={String(lt.id)}>
                    {lt.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Substitute (optional)"
                value={returnedForm.substitute_id}
                onChange={(e) =>
                  handleReturnedFormChange("substitute_id", e.target.value)
                }
              >
                <MenuItem value="">None</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={String(emp.id)}>
                    {emp.name} ({emp.employee_code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date"
                InputLabelProps={{ shrink: true }}
                value={returnedForm.start_date}
                onChange={(e) =>
                  handleReturnedFormChange("start_date", e.target.value)
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date"
                InputLabelProps={{ shrink: true }}
                value={returnedForm.end_date}
                onChange={(e) =>
                  handleReturnedFormChange("end_date", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                SelectProps={{
                  multiple: true,
                  renderValue: (selected) => {
                    const selectedSet = new Set(selected as string[]);
                    return employees
                      .filter((emp) => selectedSet.has(String(emp.id)))
                      .map((emp) => `${emp.name} (${emp.employee_code})`)
                      .join(", ");
                  },
                }}
                label="People To Notify"
                value={returnedForm.people_to_notify}
                onChange={(e) =>
                  handleReturnedFormChange(
                    "people_to_notify",
                    e.target.value as unknown as string[],
                  )
                }
              >
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={String(emp.id)}>
                    {emp.name} ({emp.employee_code})
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={3}
                label="Reason"
                value={returnedForm.reason}
                onChange={(e) =>
                  handleReturnedFormChange("reason", e.target.value)
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Existing Attachments
              </Typography>
              {leave?.attachments && leave.attachments.length > 0 ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {leave.attachments.map((attachment: any) => (
                    <FormControlLabel
                      key={attachment.id}
                      control={
                        <Checkbox
                          checked={removeAttachmentIds.includes(attachment.id)}
                          onChange={() => toggleRemoveAttachment(attachment.id)}
                        />
                      }
                      label={`Remove ${attachment.original_filename}`}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No existing attachments.
                </Typography>
              )}
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Add New Attachments
              </Typography>
              <TextField
                fullWidth
                type="file"
                inputProps={{ multiple: true }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const files = e.target.files
                    ? Array.from(e.target.files)
                    : [];
                  setNewAttachments(files);
                }}
              />
              {newAttachments.length > 0 && (
                <Typography variant="caption" color="textSecondary">
                  {newAttachments.length} new file(s) selected
                </Typography>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setReturnedEditOpen(false)}
            disabled={returnedEditLoading || resubmitLoading}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleReviseReturnedApplication}
            disabled={returnedEditLoading || resubmitLoading}
          >
            {returnedEditLoading ? "Saving..." : "Save Changes"}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={handleSubmitEditedReturnedApplication}
            disabled={returnedEditLoading || resubmitLoading}
          >
            {returnedEditLoading || resubmitLoading
              ? "Submitting Edited Version..."
              : "Submit Revised Application"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancel Leave Dialog (for pending/partially_cancelled) */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Leave</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            You can cancel all or part of this leave application.
            {leave && (
              <>
                {" "}
                Total days: <strong>{leave.number_of_days}</strong>
                {leave.cancelled_days > 0 && (
                  <>
                    , already cancelled: <strong>{leave.cancelled_days}</strong>
                    , remaining:{" "}
                    <strong>
                      {leave.number_of_days - (leave.cancelled_days || 0)}
                    </strong>
                  </>
                )}
              </>
            )}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Days to Cancel (optional if selecting dates below)"
            value={daysToCancel}
            onChange={(e) => setDaysToCancel(e.target.value)}
            inputProps={{
              min: 0.5,
              max: leave
                ? leave.number_of_days - (leave.cancelled_days || 0)
                : undefined,
              step: 0.5,
            }}
            helperText={`Enter the number of days to cancel, or leave empty to cancel all remaining days`}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Back</Button>
          <Button
            onClick={handleCancel}
            variant="contained"
            color="warning"
            disabled={actionLoading}
          >
            {actionLoading
              ? "Cancelling..."
              : daysToCancel
                ? `Cancel ${daysToCancel} Day(s)`
                : "Cancel All"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Cancellation Request Dialog (for approved/partially_cancelled) */}
      <Dialog
        open={cancellationDialogOpen}
        onClose={() => setCancellationDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Request Leave Cancellation</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, mt: 1 }}>
            Please provide a reason for cancelling this leave. The request will
            go through the same approval chain.
            {leave && (
              <>
                {" "}
                Total days: <strong>{leave.number_of_days}</strong>
                {leave.cancelled_days > 0 && (
                  <>
                    , already cancelled: <strong>{leave.cancelled_days}</strong>
                    , remaining:{" "}
                    <strong>
                      {leave.number_of_days - (leave.cancelled_days || 0)}
                    </strong>
                  </>
                )}
              </>
            )}
          </Typography>
          <TextField
            fullWidth
            type="number"
            label="Days to Cancel (optional if selecting specific dates below)"
            value={daysToCancel}
            onChange={(e) => setDaysToCancel(e.target.value)}
            inputProps={{
              min: 0.5,
              max: leave
                ? leave.number_of_days - (leave.cancelled_days || 0)
                : undefined,
              step: 0.5,
            }}
            helperText={`Enter days directly, or pick discrete dates below to auto-calculate.`}
            sx={{ mb: 2 }}
          />
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                type="date"
                label="Pick Cancellation Date"
                value={cancelDateInput}
                onChange={(e) => setCancelDateInput(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: leave
                    ? new Date(leave.start_date).toISOString().split("T")[0]
                    : undefined,
                  max: leave
                    ? new Date(leave.end_date).toISOString().split("T")[0]
                    : undefined,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                fullWidth
                variant="outlined"
                sx={{ height: "56px" }}
                onClick={addCancellationDate}
                disabled={!cancelDateInput}
              >
                Add Date
              </Button>
            </Grid>
          </Grid>
          {selectedCancelDates.length > 0 && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedCancelDates.length} discrete working day(s) selected
                for cancellation.
              </Alert>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                {selectedCancelDates.map((date) => (
                  <Chip
                    key={date}
                    label={new Date(date).toLocaleDateString()}
                    onDelete={() => removeCancellationDate(date)}
                    color="warning"
                    variant="outlined"
                  />
                ))}
              </Box>
            </>
          )}
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={3}
            label="Cancellation Reason"
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="Enter the reason for cancellation..."
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancellationDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleRequestCancellation}
            variant="contained"
            color="error"
            disabled={actionLoading || !cancellationReason.trim()}
          >
            {actionLoading ? "Submitting..." : "Submit Cancellation Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <ActionConfirmationDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        actionLabel={confirmDialog.actionLabel}
        actionColor={confirmDialog.actionColor}
        summary={confirmDialog.summary}
        onConfirm={handleConfirmAction}
        onCancel={() => {
          setConfirmDialog({ ...confirmDialog, open: false });
          setActionComments("");
        }}
        loading={actionLoading}
      >
        <TextField
          fullWidth
          multiline
          rows={3}
          label={
            confirmDialog.actionType === "return"
              ? "Reason for Return (Required)"
              : "Comments (Optional)"
          }
          value={actionComments}
          onChange={(e) => setActionComments(e.target.value)}
          placeholder={
            confirmDialog.actionType === "return"
              ? "Explain why this application is being returned..."
              : "Add any comments..."
          }
          required={confirmDialog.actionType === "return"}
          sx={{ mt: 1 }}
        />
      </ActionConfirmationDialog>
    </Box>
  );
};

export default LeaveDetails;
